import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { QuestionLayout } from "@src/packages/medical-history/components";
import { DateField } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { useMenstrualHealth } from "../MenstrualHealthContext";
import { buildMenstrualHealthNavigationStack } from "../buildMenstrualHealthNavigationStack";
import { deriveCycleLengthRange } from "../deriveCycleLengthRange";
import type { MenstrualHealthStackParamList } from "../MenstrualHealthNavigator";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

export function Last3PeriodsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useMenstrualHealth();
  const navigation =
    useNavigation<NavigationProp<MenstrualHealthStackParamList>>();
  const theme = useMedicalHistoryTheme();

  const handleSave = async () => {
    const cycleLengthRange = deriveCycleLengthRange([
      state.period1StartDate,
      state.period2StartDate,
      state.period3StartDate,
    ]);
    const updates = cycleLengthRange ?? {};

    if (cycleLengthRange) updateState(cycleLengthRange);
    await save(updates);

    const nextState = { ...state, ...updates };
    const stack = buildMenstrualHealthNavigationStack(nextState);
    const currentIdx = stack.indexOf("Last3Periods");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.menstrual_health.last_3_periods_title")}
      subtitle={t("med_history.gynae.menstrual_health.last_3_periods_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.gynae.menstrual_health.continue")}
    >
      <View style={styles.container}>
        {/* Period 1 */}
        <View style={styles.periodGroup}>
          <Text
            style={{ color: theme.cardLabel }}
            className="mb-2 pl-1 text-xs font-medium tracking-wide"
          >
            {t("med_history.gynae.menstrual_health.period_label", {
              params: { number: "1" },
            })}
          </Text>
          <View style={styles.dateRow}>
            <View style={styles.dateField}>
              <DateField
                label="Start"
                value={state.period1StartDate ?? ""}
                onChange={(iso) =>
                  updateState({ period1StartDate: iso || null })
                }
              />
            </View>
            <View style={styles.dateField}>
              <DateField
                label="End"
                value={state.period1EndDate ?? ""}
                onChange={(iso) => updateState({ period1EndDate: iso || null })}
              />
            </View>
          </View>
        </View>

        {/* Period 2 */}
        <View style={styles.periodGroup}>
          <Text
            style={{ color: theme.cardLabel }}
            className="mb-2 pl-1 text-xs font-medium tracking-wide"
          >
            {t("med_history.gynae.menstrual_health.period_label", {
              params: { number: "2" },
            })}
          </Text>
          <View style={styles.dateRow}>
            <View style={styles.dateField}>
              <DateField
                label="Start"
                value={state.period2StartDate ?? ""}
                onChange={(iso) =>
                  updateState({ period2StartDate: iso || null })
                }
              />
            </View>
            <View style={styles.dateField}>
              <DateField
                label="End"
                value={state.period2EndDate ?? ""}
                onChange={(iso) => updateState({ period2EndDate: iso || null })}
              />
            </View>
          </View>
        </View>

        {/* Period 3 */}
        <View style={styles.periodGroup}>
          <Text
            style={{ color: theme.cardLabel }}
            className="mb-2 pl-1 text-xs font-medium tracking-wide"
          >
            {t("med_history.gynae.menstrual_health.period_label", {
              params: { number: "3" },
            })}
          </Text>
          <View style={styles.dateRow}>
            <View style={styles.dateField}>
              <DateField
                label="Start"
                value={state.period3StartDate ?? ""}
                onChange={(iso) =>
                  updateState({ period3StartDate: iso || null })
                }
              />
            </View>
            <View style={styles.dateField}>
              <DateField
                label="End"
                value={state.period3EndDate ?? ""}
                onChange={(iso) => updateState({ period3EndDate: iso || null })}
              />
            </View>
          </View>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  periodGroup: {
    marginBottom: 20,
  },
  dateRow: {
    flexDirection: "row",
    gap: 12,
  },
  dateField: {
    flex: 1,
  },
});
